<template lang="pug">
ValidationObserver(ref="dialogObserver")
  AppTabs.member-settings(
    v-if="!showDialogLoading"
    ref="tabsComponent"
    :class="{ 'is-muted pointer-events-none': showDialogLoading }"
    compact
    masthead-present
    :toggled="menuVisible"
    :selected-tab="selectedTab"
    :is-scrolled="isScrolled"
    @change="handleTabChange"
  )
    template(#masthead)
      slot(name="masthead")
      .tab-menu-launcher.align-items-center.gap-10(
        ref="menuToggle"
        @click="toggleMenu"
      )
        | {{ selectedTabLabel }}
        i.hi.hi-caret.hi-10.text-grey
    template(#header)
      MemberProfileInfo
    AppTab(
      v-if="!showOnlyPaymentInfoTab"
      label="Info"
      tab-id="info"
      :has-error="validateTab('info')"
    )
      ValidationObserver(ref="info")
        MemberDialogTabAbout
    AppTab(
      v-if="!showOnlyPaymentInfoTab"
      label="Employment"
      tab-id="employment"
      :has-error="validateTab('employment')"
    )
      ValidationObserver(ref="employment")
        MemberDialogTabEmployment
    AppTab(
      v-if="showTeamsUI && !showOnlyPaymentInfoTab"
      label="Teams"
      tab-id="teams"
      :has-error="validateTab('teams')"
    )
      ValidationObserver(ref="teams")
        MemberDialogTabTeams
    AppTab(
      v-if="!showOnlyPaymentInfoTab"
      :label="projectsRolesLabel"
      tab-id="projects-and-roles"
      :has-error="validateTab('projectsRoles')"
    )
      ValidationObserver(ref="projectsRoles")
        MemberDialogTabProjectsRolesNew(:processing-in-background="processingInBackground")
    AppTab(
      v-if="canEditBilling"
      label="Pay / Bill"
      tab-id="pay / bill"
      :has-error="validateTab('payments')"
    )
      ValidationObserver(ref="payments")
        MemberDialogTabPayments
          template(#actions)
            .mx-10
              slot(name="actions")

    AppTab(
      v-if="!showOnlyPaymentInfoTab"
      label="Work Time & Limits"
      tab-id="work hours"
      :has-error="validateTab('limits')"
    )
      ValidationObserver(ref="limits")
        MemberDialogTabLimits
    AppTab(
      v-if="!showOnlyPaymentInfoTab"
      label="Settings"
      tab-id="settings"
      :has-error="validateTab('settings')"
    )
      ValidationObserver(ref="settings")
        MemberDialogTabSettings
  slot(
    v-if="!hideButtonForPayments"
    name="actions"
  )
    .modal-footer.mt-30.p-0
      button.btn.btn-default(@click="closeDialog") Cancel
      button.btn.btn-primary(
        :disabled="submitting"
        @click.stop.prevent="handleSubmit"
      )
        | {{ !submitting ? 'Save' : 'Saving...' }}
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { isEmpty, omit } from 'lodash-es';
import { handleFormErrors } from '~/utils/form_utils';
import { validatePayPeriodState } from '~/utils/validate_pay_period_state';
import MemberDialogTabAbout from './MemberDialogTabAbout.vue';
import MemberDialogTabEmployment from './MemberDialogTabEmployment.vue';
import MemberDialogTabSettings from './MemberDialogTabSettings.vue';
import MemberDialogTabTeams from './MemberDialogTabTeams.vue';
import MemberDialogTabProjectsRolesNew from './MemberDialogTabProjectsRolesNew.vue';
import MemberDialogTabPayments from './MemberDialogTabPayments.vue';
import MemberDialogTabLimits from './MemberDialogTabLimits.vue';
import MemberProfileInfo from './MemberProfileInfo.vue';


const TAB_ID_TO_OBSERVER = {
  info: 'info',
  employment: 'employment',
  teams: 'teams',
  'projects-and-roles': 'projectsRoles',
  'pay / bill': 'payments',
  'work hours': 'limits',
  settings: 'settings',
};
const TAB_OBSERVERS = Object.values(TAB_ID_TO_OBSERVER);
// Backend error keys that are not tab fields (handled via appMessage / address handlers
// in TheOrganizationMemberDialog) — they must never mark a tab as errored.
const NON_TAB_ERROR_KEYS = ['pay_rate_note', 'bill_rate_note', 'home_address', 'work_address', 'base'];

export default {
  name: 'MemberSettings',
  components: {
    MemberDialogTabAbout,
    MemberDialogTabEmployment,
    MemberDialogTabSettings,
    MemberDialogTabTeams,
    MemberDialogTabProjectsRolesNew,
    MemberDialogTabPayments,
    MemberDialogTabLimits,
    MemberProfileInfo,
  },
  props: {
    selectedTab: {
      type: String,
      required: true,
    },
    processingInBackground: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      menuVisible: false,
      tabWithBackendErrors: null,
      clickOutsideHandler: null,
      isScrolled: false,
      tabLabels: {
        'projects-and-roles': 'projectsRolesLabel'
      },
    };
  },
  computed: {
    ...mapState(['errors', 'membership', 'organization', 'submitting']),
    ...mapGetters([
      'showTeamsUI',
      'memberAvatarData',
      'membershipData',
      'paymentsData',
      'membershipUser',
      'limitsEnabled',
      'currentUserManagePaymentInfo',
      'showOnlyPaymentInfoTab',
      'canEditBilling',
      'showDialogLoading',
      'hasFeature',
      'expectedWorkEnabled',
    ]),
    projectsRolesLabel() {
      if (this.hasFeature('projects')) {
        return 'Roles';
      }

      return 'Role';
    },
    hideButtonForPayments() {
      return this.selectedTab === 'payments';
    },
    selectedTabLabel() {
      const label = this.tabLabels[this.selectedTab];

      return label ? this[label] || label : this.selectedTab;
    }
  },
  mounted() {
    // Keep a reference so beforeUnmount can actually remove this listener — an anonymous handler
    // can't be removed, and `removeEventListener('click')` with no handler throws under strict jsdom.
    this.onDocumentClick = (event) => {
      if (this.menuVisible && !this.$refs.menuToggle.contains(event.target)) {
        this.menuVisible = false;
      }
    };
    document.addEventListener('click', this.onDocumentClick);

    window.addEventListener('beforeunload', this.checkFormDirty);

    this.setupTabsObserver();
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('beforeunload', this.checkFormDirty);

    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    ...mapActions(['loadTable', 'updateMembership', 'setSubmitting', 'resetFields']),
    validateCustomPayPeriodSelection() {
      if (!this.canEditBilling || !this.organization?.custom_pay_period_dates_enabled) {
        return true;
      }

      const message = validatePayPeriodState({
        payPeriodId: this.membership?.payments?.pay_period,
        periodWeekDay: this.membership?.payments?.period_week_day,
        periodStartDay: this.membership?.payments?.period_start_day,
        periodSecondStartDay: this.membership?.payments?.period_second_start_day,
        periodAnchorDate: this.membership?.payments?.period_anchor_date,
      });

      if (!message) {
        return true;
      }

      this.$appMessage(message);

      return false;
    },
    closeDialog() {
      this.$emit('cancel');
      this.resetFields();
    },
    validateTab(tabId) {
      if (this.tabWithBackendErrors === tabId) {
        return true;
      }
      if (this.$refs.dialogObserver && this.$refs.dialogObserver.flags.invalid) {
        const tab = this.$refs[`${tabId}`];

        return tab && tab.flags.failed;
      }
    },
    handleTabChange(...args) {
      this.$emit('tab-selected', ...args);
    },
    clearBackendErrors() {
      this.tabWithBackendErrors = null;
    },
    async handleSubmit() {
      this.clearBackendErrors();
      const validated = await this.$refs.dialogObserver.validate();

      if (validated) {
        if (!this.validateCustomPayPeriodSelection()) {
          return;
        }

        this.setSubmitting(true);
        this.updateMembership({ data: this.membershipData }).then(this.completeRequest, this.handleErrors);
      }
    },
    async handleTabSubmit() {
      this.clearBackendErrors();
      const validated = await this.$refs.payments.validate();
      const tabData = `${this.selectedTab}Data`;

      if (validated) {
        if (!this.validateCustomPayPeriodSelection()) {
          return;
        }

        this.setSubmitting(true);
        this.updateMembership({ data: this[tabData], tab: this.selectedTab }).then(
          this.completeRequest,
          this.handleErrors,
        );
      }
    },
    completeRequest(response) {
      this.setSubmitting(false);

      if (response && response.success) {
        this.handleSuccess();
      } else {
        this.handleErrors();
      }
    },
    handleSuccess() {
      this.$appMessage('Information updated');
      this.closeDialog();
      this.loadTable();
    },
    // vee-validate v4 setErrors only reaches fields registered on the observer it's called on,
    // so backend save errors go to the active tab's observer (deeper fields are applied by their
    // owner, e.g. ExpectedWorkDaysForm) and the tab is marked red explicitly — v3 did that via
    // observer flags, which v4 only refreshes on validate(). Also called by
    // TheOrganizationMemberDialog, which owns the page's save flow.
    applyBackendErrors(errors) {
      const observer = TAB_ID_TO_OBSERVER[this.selectedTab];
      const fieldErrors = omit(errors, NON_TAB_ERROR_KEYS);

      if (!observer || isEmpty(fieldErrors)) {
        return;
      }

      this.$refs[observer]?.setErrors(fieldErrors);
      this.tabWithBackendErrors = observer;
    },
    handleErrors() {
      this.applyBackendErrors(this.errors);
      handleFormErrors(this.$refs.dialogObserver, this.errors, this.$appMessage);
    },
    toggleMenu() {
      this.menuVisible = !this.menuVisible;
    },
    checkFormDirty(e) {
      const isAnyFormDirty = TAB_OBSERVERS.some((ref) => this.$refs[ref]?.flags.dirty);

      if (isAnyFormDirty) {
        e.preventDefault();

        e.returnValue = '';
      }
    },
    setupTabsObserver() {
      const observer = new IntersectionObserver(
        ([entry]) => {
          this.isScrolled = !entry.isIntersecting;
        },
        { rootMargin: '-17px 0px 0px 0px', threshold: 1 },
      );
      const target = document.getElementById('members-back-nav');

      if (target) {
        observer.observe(target);
      }
    },

    focusFailedTab() {
      const failedTab = this.$refs.tabsComponent.tabs.find((tab) => tab.hasError);

      if (failedTab) {
        this.$parent.$emit('tab-selected', failedTab.tabId);
      }
    },
  },
};
</script>
